import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useAtomValue } from 'jotai';
import { Fab, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { selectedBranchAtom, userBranchesAtom } from '../../../Atoms';
import UserImapsListItems from './Components/UserImapsListItems';
import useAPI from '../../../api';
import UserImapsDialog from './Components/UserImapsDialog';
import useNotify from '../../../Hooks/useNotify';

const UserImapsPage = function UserImapsPage({ searchedValue }) {
  const intl = useIntl();
  const API = useAPI();

  const { notify, notifyError } = useNotify();
  const { id: defaultBranchId, branchDescription: defaultBranchDescription } =
    useAtomValue(selectedBranchAtom) || {};

  const [filteredBranches, setFilteredBranches] = useState([]);
  const userBranches = useAtomValue(userBranchesAtom);
  const [userImaps, setUserImaps] = useState(null);
  const [newDialog, setNewDialog] = useState(null);
  const [editDialog, setEditDialog] = useState(null);
  const [dialogId, setDialogId] = useState('');
  const [title, setTitle] = useState('');

  useEffect(() => {
    API.getUserImaps(
      (res) => {
        setUserImaps(res);
      },
      () => {
        notify('ERROR_LOADING_USERIMAPS', 'error');
      }
    );
  }, []);

  useEffect(() => {
    if (userBranches && userImaps) {
      const filtered = userBranches.filter(
        (branch) =>
          !userImaps.some((userImap) => userImap.branchId === branch.id)
      );
      setFilteredBranches(filtered);
    }
  }, [userBranches, userImaps]);

  const openNewUserImapsDialog = () => {
    setDialogId('newUserImap');
    setNewDialog(true);
  };

  const openEditUserImapDialog = (userImap) => {
    setDialogId('editUserImap');
    setEditDialog(userImap);
    setTitle(userImap.branchDescription);
  };

  const closeUserImapDialog = () => {
    setEditDialog(null);
    setNewDialog(null);
  };

  const handleNewUserImapSubmit = (newUserImap) => {
    const userImapExists = userImaps.some(
      (item) => item.branchId === newUserImap.branchId
    );

    if (userImapExists) {
      notify('EXISTING_USERIMAP', 'error');
      return;
    }
    API.createUserImap(
      newUserImap,
      () => {
        setUserImaps((prev) => [newUserImap, ...prev]);
        setNewDialog(null);
        notify('NEW_USERIMAP_CREATE_SUCCESSFULLY', 'success');
      },
      (err) => {
        notifyError('CREATE_USERIMAP_ERROR', err);
      }
    );
  };

  const handleEditSubmit = (editUserImap) => {
    API.editUserImap(
      editUserImap,
      () => {
        setUserImaps((prevUserImap) =>
          prevUserImap.map((item) =>
            item.branchId === editUserImap.branchId ? editUserImap : item
          )
        );
        setEditDialog(null);
        notify('USERIMAP_EDITED_SUCCESSFULLY', 'success');
      },
      (err) => {
        notifyError('EDITING_USERIMAP_ERROR', err);
      }
    );
  };

  return (
    <div style={{ overflow: 'auto' }}>
      <Tooltip title={intl.messages.NEW_USERIMAP}>
        <Fab
          color="primary"
          aria-label={intl.messages.NEW_USERIMAP}
          onClick={openNewUserImapsDialog}
          sx={{
            position: 'absolute',
            bottom: 30,
            right: 30,
          }}
          disabled={filteredBranches.length === 0}
        >
          <AddIcon />
        </Fab>
      </Tooltip>
      <UserImapsListItems
        userImaps={userImaps}
        onClick={openEditUserImapDialog}
        searchedValue={searchedValue}
      />
      <UserImapsDialog
        branches={filteredBranches}
        dialogId={dialogId}
        title={intl.messages.NEW_USERIMAP}
        open={Boolean(newDialog)}
        userImap={newDialog}
        defaultBranchId={defaultBranchId}
        defaultBranchDescription={defaultBranchDescription}
        handleClose={closeUserImapDialog}
        onSubmit={handleNewUserImapSubmit}
      />
      <UserImapsDialog
        dialogId={dialogId}
        title={`${intl.messages.EDIT_USERIMAP} ${title}`}
        open={Boolean(editDialog)}
        userImap={editDialog}
        handleClose={closeUserImapDialog}
        onSubmit={handleEditSubmit}
      />
    </div>
  );
};

export default UserImapsPage;
